import * as React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  AddCircleRounded,
  CheckCircleRounded,
  GroupAddRounded,
  RefreshRounded,
  ThumbUpAltRounded,
} from '@mui/icons-material';

import {
  ApiError,
  assignCongregationRole,
  createCongregationMemberFromUser,
  createCongregationMemberWithProfile,
  fetchAdminUsers,
  fetchCongregationAdminApplications,
  fetchCongregationMasters,
  lookupCongregationMemberByUserId,
  updateCongregationSpiritualGuide,
  type AdminUser,
  type CongregationAdminFilter,
} from '../../api';
import states from '../../data/states.json';
import { MARITAL_STATUS_OPTIONS } from '../../data/congregationOptions';
import { useCongregationAccess } from '../../congregation/context/CongregationAccessContext';
import { normalizeMastersPayload } from '../../congregation/utils';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import { useTheme } from '@mui/joy/styles';
import type {
  CongregationApplication,
  CongregationMastersPayload,
  CongregationMember,
  CongregationMemberSearchResult,
  CongregationRoleAssignmentInput,
  CongregationSpiritualGuide,
} from '../../types/congregation';

interface PromoteState {
  open: boolean;
  searchTerm: string;
  results: AdminUser[];
  selectedUser: AdminUser | null;
  manualMemberId: string;
  loading: boolean;
  submitting: boolean;
  showProfileForm: boolean;
  profileData: {
    displayName: string;
    legalName: string;
    phone: string;
    email: string;
    occupation: string;
    city: string;
    state: string;
    country: string;
    gender: string;
    maritalStatus: string;
    spiritualMasterId: number | null;
    whatsappOptIn: boolean;
  };
}

const initialPromoteState: PromoteState = {
  open: false,
  searchTerm: '',
  results: [],
  selectedUser: null,
  manualMemberId: '',
  loading: false,
  submitting: false,
  showProfileForm: false,
  profileData: {
    displayName: '',
    legalName: '',
    phone: '',
    email: '',
    occupation: '',
    city: '',
    state: '',
    country: 'India',
    gender: '',
    maritalStatus: '',
    spiritualMasterId: null,
    whatsappOptIn: true,
  },
};

const MIN_PROMOTE_SEARCH_LENGTH = 3;
const PROMOTE_SEARCH_MAX_LENGTH = 120;

function sortGuides(guides: CongregationSpiritualGuide[]) {
  return [...guides].sort((a, b) => a.memberDisplayName.localeCompare(b.memberDisplayName));
}

function countAssigned(applications: CongregationApplication[], mentorMemberId: number) {
  return applications.filter((app) => app.assignedMentorId === mentorMemberId).length;
}

export default function CongregationMentorStaffingPage() {
  const { membership } = useCongregationAccess();
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';

  const [masters, setMasters] = React.useState<CongregationMastersPayload | null>(null);
  const [applications, setApplications] = React.useState<CongregationApplication[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [promoteState, setPromoteState] = React.useState<PromoteState>(initialPromoteState);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const params: CongregationAdminFilter = { status: 'ASSIGNED_TO_MENTOR' };
      const [mastersResponse, applicationsResponse] = await Promise.all([
        fetchCongregationMasters(),
        fetchCongregationAdminApplications(params),
      ]);
      setMasters(normalizeMastersPayload(mastersResponse));
      setApplications(applicationsResponse.applications ?? []);
    } catch (error) {
      console.error('Failed to load caretaker staffing data', error);
      showSnackbar('Unable to load caretaker staffing data.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const refreshApplications = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchCongregationAdminApplications({ status: 'ASSIGNED_TO_MENTOR' });
      setApplications(response.applications ?? []);
    } catch (error) {
      console.error('Failed to refresh caretaker assignments', error);
      showSnackbar('Unable to refresh caretaker assignments.', 'warning');
    } finally {
      setIsRefreshing(false);
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const allGuides = React.useMemo(
    () => sortGuides(masters?.spiritualGuides ?? []),
    [masters?.spiritualGuides],
  );

  const filteredMentors = React.useMemo(() => {
    return allGuides;
  }, [allGuides]);

  const activeGuides = React.useMemo(
    () => allGuides.filter((guide) => guide.isActive !== false),
    [allGuides],
  );
  const mentorSummary = React.useMemo(() => {
    const total = allGuides.length;
    const active = activeGuides.length;
    const paused = Math.max(total - active, 0);
    const assigned = applications.length;
    const withCapacity = allGuides.filter((guide) => guide.capacity != null).length;
    return { total, active, paused, assigned, withCapacity };
  }, [allGuides, activeGuides.length, applications.length]);

  const handleToggleActive = React.useCallback(
    async (guide: CongregationSpiritualGuide, nextValue: boolean) => {
      try {
        await updateCongregationSpiritualGuide(guide.id, {
          memberId: guide.memberId,
          memberDisplayName: guide.memberDisplayName,
          bio: guide.bio ?? undefined,
          isActive: nextValue,
        });
        showSnackbar(`Mentor ${nextValue ? 'activated' : 'paused'}.`, 'success');
        await loadData();
      } catch (error) {
        console.error('Failed to toggle caretaker availability', error);
        if (error instanceof ApiError && error.status === 409) {
          showSnackbar('Caretaker availability could not be changed because of a conflicting update.', 'warning');
          await loadData();
        } else {
          showSnackbar('Unable to update caretaker availability.', 'danger');
        }
      }
    },
    [loadData, showSnackbar],
  );

  const handlePromoteSearch = React.useCallback(
    async (term: string) => {
      const trimmed = term.trim().slice(0, PROMOTE_SEARCH_MAX_LENGTH);
      if (trimmed.length < MIN_PROMOTE_SEARCH_LENGTH) {
        setPromoteState((prev) => ({ ...prev, results: [], loading: false }));
        return;
      }
      setPromoteState((prev) => ({ ...prev, loading: true }));
      try {
        const users = await fetchAdminUsers(trimmed, 20);
        // Backend already filters out users who are already mentors
        setPromoteState((prev) => ({ ...prev, results: users ?? [] }));
      } catch (error) {
        console.error('Failed to search users', error);
        showSnackbar('Unable to search users. Please refine the query.', 'warning');
      } finally {
        setPromoteState((prev) => ({ ...prev, loading: false }));
      }
    },
    [showSnackbar],
  );

  React.useEffect(() => {
    if (
      promoteState.open &&
      promoteState.searchTerm.trim().length >= MIN_PROMOTE_SEARCH_LENGTH
    ) {
      handlePromoteSearch(promoteState.searchTerm);
    }
  }, [handlePromoteSearch, promoteState.open, promoteState.searchTerm]);

  const handlePromoteSubmit = React.useCallback(async () => {
    const manualId = promoteState.manualMemberId.trim();
    
    // CASE 1: User selected from search results
    if (promoteState.selectedUser && !manualId) {
      try {
        setPromoteState((prev) => ({ ...prev, submitting: true }));

        // Check if user is already a congregation member
        const lookupResult = await lookupCongregationMemberByUserId(
          promoteState.selectedUser.id,
        );

        // Case 1a: User IS a congregation member - assign role directly
        const memberId = lookupResult.memberId;
        const payload: CongregationRoleAssignmentInput = {
          memberId,
          role: 'MENTOR',
        };
        await assignCongregationRole(payload);
        showSnackbar('Caretaker role assigned.', 'success');
        setPromoteState(initialPromoteState);
        await loadData();
      } catch (error) {
        // Case 1b: User is NOT a congregation member - show profile form
        if (error instanceof ApiError && error.status === 404) {
          setPromoteState((prev) => ({
            ...prev,
            submitting: false,
            showProfileForm: true,
            profileData: {
              displayName: prev.selectedUser?.displayName || '',
              legalName:
                prev.selectedUser?.legalName ||
                prev.selectedUser?.displayName ||
                '',
              phone: prev.selectedUser?.phone || '',
              email: prev.selectedUser?.email || '',
              occupation: '',
              city: prev.selectedUser?.city || '',
              state: prev.selectedUser?.state || '',
              country: prev.selectedUser?.country || 'India',
              gender: '',
              spiritualMasterId: null,
              maritalStatus: '',
              whatsappOptIn: true,
            },
          }));
          showSnackbar(
            'This user is not yet a congregation member. Please complete their profile to proceed.',
            'warning',
          );
          return;
        }

        // Handle other errors
        console.error('Failed to promote user', error);
        if (error instanceof ApiError) {
          if (error.status === 409) {
            showSnackbar(
              'That user already has a caretaker assignment. Refreshed the roster.',
              'warning',
            );
            await loadData();
          } else {
            showSnackbar(
              'Unable to promote this user. Please retry.',
              'danger',
            );
          }
        } else {
          showSnackbar(
            'Unable to promote this user. Please retry.',
            'danger',
          );
        }
      } finally {
        setPromoteState((prev) => ({ ...prev, submitting: false }));
      }
      return;
    }

    // CASE 2: Manual congregation member ID entry
    if (!manualId) {
      showSnackbar(
        'Please select a user from search or provide a congregation member ID.',
        'warning',
      );
      return;
    }

    const memberId = Number(manualId);
    if (Number.isNaN(memberId)) {
      showSnackbar('Please provide a valid congregation member ID.', 'warning');
      return;
    }

    const payload: CongregationRoleAssignmentInput = {
      memberId,
      role: 'MENTOR',
    };
    try {
      setPromoteState((prev) => ({ ...prev, submitting: true }));
      await assignCongregationRole(payload);
      showSnackbar('Caretaker role assigned.', 'success');
      setPromoteState(initialPromoteState);
      await loadData();
    } catch (error) {
      console.error('Failed to assign caretaker role', error);
      if (error instanceof ApiError) {
        if (error.status === 409) {
          showSnackbar(
            'That member already has a caretaker assignment. Refreshed the roster.',
            'warning',
          );
          await loadData();
        } else if (error.status === 404) {
          showSnackbar(
            'Congregation member not found. Please check the member ID.',
            'danger',
          );
        } else {
          showSnackbar('Unable to assign caretaker role. Please retry.', 'danger');
        }
      } else {
        showSnackbar('Unable to assign caretaker role. Please retry.', 'danger');
      }
    } finally {
      setPromoteState((prev) => ({ ...prev, submitting: false }));
    }
  }, [loadData, promoteState.selectedUser, promoteState.manualMemberId, showSnackbar]);

  const handleCompleteProfileAndPromote = React.useCallback(async () => {
    const manualId = promoteState.manualMemberId.trim();
    const targetId = promoteState.selectedUser?.id ?? (manualId ? Number(manualId) : null);
    if (!targetId || Number.isNaN(targetId)) {
      showSnackbar('Select a user or provide a valid user ID.', 'warning');
      return;
    }
    // Validate required fields
    const { legalName, displayName, phone, email, occupation, city, state, gender, maritalStatus, spiritualMasterId } = promoteState.profileData;
    if (!legalName.trim()) {
      showSnackbar('Legal name is required.', 'warning');
      return;
    }
    if (!displayName.trim()) {
      showSnackbar('Preferred display name is required.', 'warning');
      return;
    }
    if (!phone.trim()) {
      showSnackbar('Phone number is required.', 'warning');
      return;
    }
    if (!email.trim()) {
      showSnackbar('Email is required.', 'warning');
      return;
    }
    if (!occupation.trim()) {
      showSnackbar('Occupation is required.', 'warning');
      return;
    }
    if (!city.trim()) {
      showSnackbar('City is required.', 'warning');
      return;
    }
    if (!state.trim()) {
      showSnackbar('State is required.', 'warning');
      return;
    }
    if (!gender) {
      showSnackbar('Gender is required.', 'warning');
      return;
    }
    if (!maritalStatus) {
      showSnackbar('Marital status is required.', 'warning');
      return;
    }
    try {
      setPromoteState((prev) => ({ ...prev, submitting: true }));
      // Create congregation member with full profile
      const response = await createCongregationMemberWithProfile({
        userId: Number(targetId),
        displayName,
        legalName,
        phone,
        email,
        occupation,
        city,
        state,
        country: promoteState.profileData.country,
        gender,
        maritalStatus,
        spiritualMasterId,
        whatsappOptIn: promoteState.profileData.whatsappOptIn,
      });
      // Extract the actual member ID from the response
      // response.member is CongregationMember with the id field
      const createdMemberId = response.member.id;
      // Now assign the mentor role using the actual member ID from the response
      const payload: CongregationRoleAssignmentInput = {
        memberId: createdMemberId,
        role: 'MENTOR',
      };
      await assignCongregationRole(payload);
      showSnackbar('Congregation profile created and caretaker role assigned.', 'success');
      setPromoteState(initialPromoteState);
      await loadData();
    } catch (error) {
      console.error('Failed to create profile and assign caretaker role', error);
      showSnackbar('Unable to complete profile and assign role. Please retry.', 'danger');
    } finally {
      setPromoteState((prev) => ({ ...prev, submitting: false }));
    }
  }, [loadData, promoteState.selectedUser, promoteState.manualMemberId, promoteState.profileData, showSnackbar]);

  return (
    <Box>
      <Stack spacing={{ xs: 2.5, md: 3.5 }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'mentors-float 8s ease-in-out infinite',
              '@keyframes mentors-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <GroupAddRounded sx={{ fontSize: 28, color: 'primary.500' }} />
                    <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                      Caretaker staffing
                    </Typography>
                  </Stack>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 760 }}>
                    Promote users into the caretaker cohort and manage their availability. Signed in as {membership.member.displayName}.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip variant="soft" color="primary">
                    {mentorSummary.total} mentors
                  </Chip>
                  <Chip variant="soft" color="success">
                    {mentorSummary.active} active
                  </Chip>
                  <Chip variant="soft" color="neutral">
                    {mentorSummary.assigned} assigned
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Availability mix
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {mentorSummary.active} active • {mentorSummary.paused} paused
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {mentorSummary.withCapacity} mentors have capacity set.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Quick actions
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                      <Button
                        startDecorator={<GroupAddRounded />}
                        variant="solid"
                        onClick={() => setPromoteState((prev) => ({ ...prev, open: true }))}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Promote caretaker
                      </Button>
                      <Button
                        variant="soft"
                        startDecorator={<RefreshRounded />}
                        onClick={loadData}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Refresh
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
          <CardContent sx={{ display: 'grid', gap: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Button
                startDecorator={<GroupAddRounded />}
                variant="solid"
                onClick={() => setPromoteState((prev) => ({ ...prev, open: true }))}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                Promote caretaker
              </Button>
              <IconButton variant="soft" onClick={loadData} aria-label="Refresh caretakers">
                <RefreshRounded />
              </IconButton>
            </Stack>

            {loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size="md" />
              </Box>
            ) : (
              <Sheet
                variant="outlined"
                sx={{ borderRadius: 'lg', overflow: 'hidden', boxShadow: 'sm', borderColor: 'divider' }}
              >
                <Table
                  size="sm"
                  stickyHeader
                  sx={{
                    '& thead th': {
                      bgcolor: 'background.level1',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>Mentor</th>
                      <th style={{ width: 140 }}>Status</th>
                      <th style={{ width: 160 }}>Assigned devotees</th>
                      <th style={{ width: 160 }}>Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMentors.length === 0 ? (
                      <tr>
                        <td colSpan={4}>
                          <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography level="body-md" color="neutral">
                              No caretakers match the selected filters.
                            </Typography>
                          </Box>
                        </td>
                      </tr>
                    ) : (
                      filteredMentors.map((guide) => {
                        const assignedCount = countAssigned(applications, guide.memberId);
                        return (
                          <tr key={guide.id}>
                            <td>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Avatar size="sm" src={guide.memberPhotoUrl || undefined}>
                                  {guide.memberDisplayName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Stack spacing={0.5}>
                                  <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                                    {guide.memberDisplayName}
                                  </Typography>
                                  <Typography level="body-xs" color="neutral">
                                    Member #{guide.memberId}
                                  </Typography>
                                  {guide.centerName ? (
                                    <Chip
                                      size="sm"
                                      variant="soft"
                                      color="primary"
                                      startDecorator={<ThumbUpAltRounded fontSize="small" />}
                                    >
                                      {guide.centerName}
                                    </Chip>
                                  ) : null}
                                </Stack>
                              </Stack>
                            </td>
                            <td>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Switch
                                  checked={guide.isActive ?? true}
                                  onChange={(event) => handleToggleActive(guide, event.target.checked)}
                                />
                                <Typography level="body-sm" color="neutral">
                                  {guide.isActive ? 'Active' : 'Paused'}
                                </Typography>
                              </Stack>
                            </td>
                            <td>
                              <Chip size="sm" variant="soft" color={assignedCount > 8 ? 'warning' : 'primary'}>
                                {assignedCount} in queue
                              </Chip>
                            </td>
                            <td>
                              <Typography level="body-sm">
                                {guide.capacity ?? 'Not set'}
                              </Typography>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </Sheet>
            )}
            {isRefreshing ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size="sm" />
                <Typography level="body-sm" color="neutral">
                  Refreshing caretaker load…
                </Typography>
              </Stack>
            ) : null}
          </CardContent>
        </Card>

        <Modal open={promoteState.open} onClose={() => setPromoteState(initialPromoteState)}>
          <ModalDialog aria-labelledby="promote-mentor-modal" sx={{ minWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <Stack spacing={2}>
              <Typography id="promote-mentor-modal" level="title-lg">
                {promoteState.showProfileForm ? 'Complete Congregation Profile' : 'Promote user to caretaker'}
              </Typography>
              {promoteState.showProfileForm ? (
                <>
                  <Typography level="body-sm" color="warning">
                    This user is not a congregation member yet. Complete their mandatory congregation profile information to assign them as a caretaker.
                  </Typography>
                  <Sheet sx={{ borderRadius: 'lg', p: 2, backgroundColor: 'background.surface' }}>
                    <Stack spacing={2}>
                      <Typography level="title-sm" fontWeight="bold">
                        Tell us about yourself
                      </Typography>
                      <FormControl>
                        <FormLabel>Legal name *</FormLabel>
                        <Input
                          value={promoteState.profileData.legalName}
                          readOnly
                          disabled
                          onChange={(event) =>
                            setPromoteState((prev) => ({
                              ...prev,
                              profileData: { ...prev.profileData, legalName: event.target.value },
                            }))
                          }
                          placeholder="Your official legal name"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Preferred display name *</FormLabel>
                        <Input
                          value={promoteState.profileData.displayName}
                          onChange={(event) =>
                            setPromoteState((prev) => ({
                              ...prev,
                              profileData: { ...prev.profileData, displayName: event.target.value },
                            }))
                          }
                          placeholder="Name visible to caretakers and admins"
                        />
                      </FormControl>
                      <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>Phone *</FormLabel>
                            <Input
                              type="tel"
                              value={promoteState.profileData.phone}
                              readOnly
                              disabled
                              onChange={(event) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, phone: event.target.value },
                                }))
                              }
                              placeholder="For caretaker communication"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>Email *</FormLabel>
                            <Input
                              type="email"
                              value={promoteState.profileData.email}
                              readOnly
                              disabled
                              onChange={(event) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, email: event.target.value },
                                }))
                              }
                              placeholder="Status updates via email"
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                      <FormControl>
                        <FormLabel>Occupation *</FormLabel>
                        <Select
                          value={promoteState.profileData.occupation}
                          onChange={(_, value) =>
                            setPromoteState((prev) => ({
                              ...prev,
                              profileData: { ...prev.profileData, occupation: value as string },
                            }))
                          }
                          placeholder="Select occupation"
                        >
                          <Option value="">Select occupation</Option>
                          <Option value="STUDENT">Student</Option>
                          <Option value="WORKING_PROFESSIONAL">Working professional</Option>
                          <Option value="SELF_EMPLOYED">Self-employed</Option>
                          <Option value="HOMEMAKER">Homemaker</Option>
                          <Option value="BUSINESS">Business</Option>
                          <Option value="RETIRED">Retired</Option>
                          <Option value="FULL_TIME_SEVA">Full-time seva</Option>
                          <Option value="UNEMPLOYED">Unemployed</Option>
                          <Option value="OTHER">Other / not listed</Option>
                        </Select>
                      </FormControl>
                      <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>City *</FormLabel>
                            <Input
                              value={promoteState.profileData.city}
                              onChange={(event) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, city: event.target.value },
                                }))
                              }
                              placeholder="Current city of residence"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>State *</FormLabel>
                            <Select
                              value={promoteState.profileData.state}
                              onChange={(_, value) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, state: value as string },
                                }))
                              }
                              placeholder="Select state"
                            >
                              <Option value="">Select state</Option>
                              {states.map((state) => (
                                <Option key={state} value={state}>
                                  {state}
                                </Option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>Gender *</FormLabel>
                            <Select
                              value={promoteState.profileData.gender}
                              onChange={(_, value) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, gender: value as string },
                                }))
                              }
                            >
                              <Option value="">Select your gender</Option>
                              <Option value="MALE">Male</Option>
                              <Option value="FEMALE">Female</Option>
                              <Option value="OTHER">Other</Option>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <FormControl>
                            <FormLabel>Marital status *</FormLabel>
                            <Select
                              value={promoteState.profileData.maritalStatus}
                              onChange={(_, value) =>
                                setPromoteState((prev) => ({
                                  ...prev,
                                  profileData: { ...prev.profileData, maritalStatus: value as string },
                                }))
                              }
                            >
                              <Option value="">Select marital status</Option>
                              {MARITAL_STATUS_OPTIONS.map((option: typeof MARITAL_STATUS_OPTIONS[0]) => (
                                <Option key={option.value} value={option.value}>
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <FormControl>
                        <FormLabel>Spiritual master</FormLabel>
                        <Select
                          placeholder="Select spiritual master"
                          value={promoteState.profileData.spiritualMasterId ?? 0}
                          onChange={(_, value) =>
                            setPromoteState((prev) => ({
                              ...prev,
                              profileData: { ...prev.profileData, spiritualMasterId: value ? Number(value) : null },
                            }))
                          }
                        >
                          <Option value={0}>None</Option>
                          {(masters?.spiritualMasters ?? []).map((master) => (
                            <Option key={master.id} value={master.id}>
                              {master.name}
                            </Option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                        <FormLabel>WhatsApp opt-in</FormLabel>
                        <Switch
                          checked={promoteState.profileData.whatsappOptIn}
                          onChange={(event) =>
                            setPromoteState((prev) => ({
                              ...prev,
                              profileData: { ...prev.profileData, whatsappOptIn: event.target.checked },
                            }))
                          }
                        />
                      </FormControl>
                    </Stack>
                  </Sheet>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="plain"
                      color="neutral"
                      onClick={() => setPromoteState(initialPromoteState)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="solid"
                      color="primary"
                      onClick={handleCompleteProfileAndPromote}
                      loading={promoteState.submitting}
                      startDecorator={<GroupAddRounded />}
                    >
                      Create Profile & Promote
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography level="body-sm" color="neutral">
                    Search for any user or enter a user ID to assign the mentor role. They will immediately gain access to mentor
                    tooling in the congregation workspace.
                  </Typography>
                  <FormControl>
                    <FormLabel>Search member</FormLabel>
                    <Input
                      placeholder="Type 3 or more characters"
                      value={promoteState.searchTerm}
                      autoFocus
                      onChange={(event) => {
                        const value = event.target.value;
                        setPromoteState((prev) => ({ ...prev, searchTerm: value }));
                        handlePromoteSearch(value);
                      }}
                      slotProps={{ input: { maxLength: PROMOTE_SEARCH_MAX_LENGTH } }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Or provide user ID</FormLabel>
                    <Input
                      placeholder="Enter user ID directly"
                      value={promoteState.manualMemberId}
                      onChange={(event) =>
                        setPromoteState((prev) => ({ ...prev, manualMemberId: event.target.value }))
                      }
                      slotProps={{ input: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 16 } }}
                    />
                  </FormControl>
                  {promoteState.loading ? (
                    <CircularProgress size="sm" />
                  ) : promoteState.results.length === 0 ? (
                    <Typography level="body-sm" color="neutral">
                      Search results will appear here.
                    </Typography>
                  ) : (
                    <Sheet variant="soft" sx={{ borderRadius: 'lg', border: '1px solid', borderColor: 'neutral.outlinedBorder' }}>
                      <Table size="sm">
                        <tbody>
                          {promoteState.results.map((user) => (
                            <tr
                              key={user.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setPromoteState((prev) => ({ ...prev, selectedUser: user }))}
                            >
                              <td>
                                <Stack spacing={0.5}>
                                  <Typography level="body-sm">{user.displayName}</Typography>
                                  <Typography level="body-xs" color="neutral">
                                    Email: {user.email}
                                  </Typography>
                                </Stack>
                              </td>
                              <td>
                                <Typography level="body-sm" color="neutral">
                                  User ID: {user.id}
                                </Typography>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant={
                                    promoteState.selectedUser?.id === user.id ? 'solid' : 'outlined'
                                  }
                                  startDecorator={<CheckCircleRounded />}
                                  onClick={() =>
                                    setPromoteState((prev) => ({ ...prev, selectedUser: user }))
                                  }
                                >
                                  Select
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Sheet>
                  )}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="plain" color="neutral" onClick={() => setPromoteState(initialPromoteState)}>
                      Cancel
                    </Button>
                    <Button
                      variant="solid"
                      onClick={handlePromoteSubmit}
                      loading={promoteState.submitting}
                      disabled={!promoteState.selectedUser && !promoteState.manualMemberId.trim()}
                    >
                      Promote
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </ModalDialog>
        </Modal>
      </Stack>
    </Box>
  );
}
